import { useRef } from 'react';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useLocale } from '@gov-nx/core/service';
import {
	NastaveniKontaktniUdajeROSItemContextProvider,
	PageCode,
	ProfilROSReklamaceContextProvider,
	useProfilROSDetailContext,
} from '@gov-nx/module/page';
import { Dialog, ErrorStatusBanner, LayoutGap } from '@gov-nx/ui/web';
import { CmsPageRender } from '../../../Cms/CmsPageRender';
import { ProfileROSComplaintSubmitButton } from '../ProfileROSComplaintSubmitButton';
import type { ProfileROSComplaintViewRef } from '../ProfileROSComplaintView';
import { ProfileROSComplaintView } from '../ProfileROSComplaintView';
import { ContactDetailsView } from './ContactDetailsView';
import { PFODataView } from './PFODataView';
import { PODataView } from './PODataView';
import { ProfileROSDetailSkeleton } from './ProfileROSDetailSkeleton';

export const ProfileROSDetailView = (
	extraConfig: ExtraConfig['profileRoutes']['rosDetailConfig']['rosDetail']
) => {
	const { detailData, controls, setControls, code, name } =
		useProfilROSDetailContext();
	const { t } = useLocale(code);
	const submitComplaintRef = useRef<ProfileROSComplaintViewRef>(null);

	const getContent = () => {
		if (controls.initialLoading) {
			return <ProfileROSDetailSkeleton />;
		}

		if (controls.initialError) {
			return <ErrorStatusBanner code={code} />;
		}

		return (
			<LayoutGap gap="m">
				{detailData?.typ === 'PFO' ? (
					<PFODataView {...extraConfig.PFODataView} />
				) : null}
				{detailData?.typ === 'PO' ? (
					<PODataView {...extraConfig.PODataView} />
				) : null}
				{detailData ? (
					<NastaveniKontaktniUdajeROSItemContextProvider
						code={PageCode['nastaveni']}
						contact={detailData}>
						<ContactDetailsView />
					</NastaveniKontaktniUdajeROSItemContextProvider>
				) : null}
				{extraConfig.isComplaintModalVisible && controls.complaintModal && (
					<ProfilROSReklamaceContextProvider
						code={code}
						data={controls.complaintModal}
						variant={controls.complaintVariant}
						onComplaintSubmit={() => setControls({ complaintModal: null })}>
						<Dialog
							isOpen={!!controls.complaintModal}
							onClose={() => setControls({ complaintModal: null })}
							accessibleCloseLabel="akce.wcag.zavrit-formular"
							title={<h3>{t('formular.podnet-k-oprave-udaju')}</h3>}
							footer={
								<ProfileROSComplaintSubmitButton
									onClick={() => submitComplaintRef.current?.submit()}
								/>
							}>
							<ProfileROSComplaintView
								ref={submitComplaintRef}
								code={code}
							/>
						</Dialog>
					</ProfilROSReklamaceContextProvider>
				)}
			</LayoutGap>
		);
	};

	return (
		<CmsPageRender
			code={code}
			showFavorite={false}
			override={{
				name: name,
				description: t('profil-ros.detail.popis'),
			}}>
			{getContent()}
		</CmsPageRender>
	);
};
